import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardBody, CardTitle, Badge } from '@/shared/ui';
import { useLogs } from '@/entities/log';
import { formatDate } from '@/shared/lib/utils';
import { 
  XCircle, 
  AlertTriangle, 
  Info, 
  Bug, 
  List, 
  ChevronRight,
  AlertCircle,
  Inbox,
  Instagram,
  Youtube,
  Facebook,
  Twitter,
  Smartphone
} from 'lucide-react';

interface LogItemProps {
  log: {
    platform: string;
    comment?: string;
    log_level?: string;
    timestamp: string;
  };
}

const LogItem: React.FC<LogItemProps> = ({ log }) => {
  const statusConfig = {
    ERROR: { variant: 'danger' as const, icon: XCircle },
    WARN: { variant: 'warning' as const, icon: AlertTriangle },
    INFO: { variant: 'default' as const, icon: Info },
    DEBUG: { variant: 'default' as const, icon: Bug },
  };

  const status = statusConfig[log.log_level as keyof typeof statusConfig] || statusConfig.INFO;

  const getPlatformIcon = (platform: string) => {
    const platforms = {
      instagram: { icon: Instagram, className: 'text-pink-500' },
      youtube: { icon: Youtube, className: 'text-red-500' },
      facebook: { icon: Facebook, className: 'text-blue-500' },
      twitter: { icon: Twitter, className: 'text-blue-400' },
    };
    return platforms[platform.toLowerCase() as keyof typeof platforms] || { icon: Smartphone, className: 'text-gray-500' };
  };

  return (
    <div className="flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors">
      <div className="flex items-center space-x-3 flex-1">
        <div className="flex items-center space-x-2">
          {React.createElement(getPlatformIcon(log.platform).icon, { 
            size: 16, 
            className: getPlatformIcon(log.platform).className 
          })}
          <Badge variant={status.variant} className="text-xs">
            {log.log_level || 'INFO'}
          </Badge>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-900 truncate">
              {log.platform}
            </span>
          </div>
          {log.comment && (
            <p className="text-xs text-gray-600 mt-1 truncate">
              {log.comment}
            </p>
          )}
        </div>
      </div>
      
      <div className="text-xs text-gray-500 ml-2 whitespace-nowrap">
        {formatDate(log.timestamp)}
      </div>
    </div>
  );
};

export const WorkLogWidget: React.FC = () => {
  // 최근 10개의 로그를 가져옵니다  
  const { data: logsResponse, isLoading, isError } = useLogs({
    page: 0,
    size: 10,
    sortBy: 'timestamp',
    sortDirection: 'desc'
  });

  const logs = logsResponse?.data?.content || [];

  return (
    <Card className="bg-gray-50/30">
      <CardHeader className="bg-white border-b border-gray-100">
        <CardTitle className="flex items-center space-x-2">
          <span>작업 로그</span>
        </CardTitle>
      </CardHeader>

      <CardBody className="p-0">
        {isLoading ? (
          <div className="space-y-3 p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <div className="flex space-x-2">
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                  <div className="w-16 h-5 bg-gray-200 rounded"></div>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                </div>
                <div className="w-16 h-3 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center p-8 text-gray-500">
            <div className="text-center">
              <AlertCircle size={32} className="mb-2 mx-auto" />
              <p>로그를 불러올 수 없습니다</p>
            </div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex items-center justify-center p-8 text-gray-500">
            <div className="text-center">
              <Inbox size={32} className="mb-2 mx-auto" />
              <p>작업 로그가 없습니다</p>
            </div>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {logs.map((log: any, index: number) => (
              <LogItem key={index} log={log} />
            ))}
          </div>
        )}
        
        {logs.length > 0 && (
          <div className="p-4 border-t border-gray-100 bg-white">
            <Link 
              to="/tasks"
              className="text-primary text-sm font-medium hover:underline inline-flex items-center"
            >
              전체 로그 보기 <ChevronRight size={16} className="ml-1" />
            </Link>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

WorkLogWidget.displayName = 'WorkLogWidget';